<template>
    <VueFinalModal
        class="flex justify-center items-center"
        :content-class="`flex flex-col max-w-4xl mx-4 p-6 bg-white border rounded-lg space-y-4 max-h-[90vh] overflow-y-auto`"
        :click-to-close="!isSubmitting"
        :esc-to-close="!isSubmitting"
        @update:model-value="val => emit('update:modelValue', val)"
    >
        <div class="flex justify-between items-center border-b pb-4">
            <h2 class="text-xl font-semibold">Záujem o spoluprácu</h2>
            <button 
                v-if="!isSubmitting"
                @click="emit('update:modelValue', false)"
                class="text-gray-500 dark:text-txt-body hover:text-gray-700 hover:dark:text-txt-body text-xl"
            >
                ×
            </button>
        </div>

        <div class="space-y-4">
            <p class="text-sm text-gray-600 dark:text-txt-body">na projekte {{ project?.title || 'Neznámy projekt' }}</p>
            
            <ProfileMissingFieldsForm
                v-if="!isProfileComplete"
                ref="missingFieldsForm"
                :missing-fields="missingFields"
                type="volunteer"
                v-model="userProfile"
                @update:model-value="updateProfile($event)"
            />

            <CFormGroup
                :error="validationResults.errors.motivation"
                label="Napíš správu organizácii, prečo a ako im pomôžeš."
                required
            >
                <CFormTextarea v-model="formData.motivation" placeholder="Začni písať správu organizácii …" />
            </CFormGroup>

            <CAlert class="h-fit" icon="info" variant="info">
                <p class="mb-0">
                    TIP: Organizácii sa väčšinou hlási viacero dobrovoľníkov. Vyberá si iba na základe tejto správy a tvojho
                    profilu. Svoje šance si zvýšiš:
                </p>
                <ul class="list-inside list-disc mt-2">
                    <li>
                        ak poriadne vyplníš svoj dobrovoľnícky GROWNi
                        <span class="link link-icon cursor-pointer" @click="showVolunteerProfilePreview">profil</span>
                    </li>
                    <li>ak konkrétne popíšeš, ako vieš na projekte pomôcť</li>
                    <li>ak vysvetlíš svoju motiváciu, prečo chceš pomôcť práve tomuto projektu</li>
                </ul>
            </CAlert>
        </div>

        <div class="flex flex-col space-y-4 pt-4 border-t">
            <CButton
                class="ml-auto w-full sm:w-auto"
                :disabled="isSubmitting"
                :loading="isSubmitting"
                size="large"
                variant="primary"
                @click="submit()"
            >
                Odoslať záujem
            </CButton>

            <div class="text-left text-sm italic text-gray-500 dark:text-txt-body">
                Beriem na vedomie, že moje hore uvedené údaje / profil, poskytne prevádzkovateľ portálu GROWNi
                organizácii {{ orgTitle }} za účelom sprostredkovania spolupráce na projekte {{ project?.title || 'tomto projekte' }}. Viac o
                <a class="link inline" href="/privacy" target="_blank">spracovaní osobných údajov</a>.
            </div>
        </div>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { useApplicationStore } from '@/stores/application'
import { validate, ValidationResults } from 'last-validator'
import cloneDeep from 'lodash-es/cloneDeep'
import { VueFinalModal, useModal } from 'vue-final-modal'
import { storeToRefs } from 'pinia'


import type { LoadingState } from '#shared/types/LoadingState'
import type { Org } from '#shared/types/Org'
import type { Project } from '#shared/types/Project'
import type { UserProfile } from '#shared/types/User'
import TooMuchRequestsModal from '@/components/modals/TooMuchRequestsModal.vue'
import VolunteerProfilePreviewModal from '@/components/volunteer/modals/VolunteerProfilePreviewModal.vue'
import { useChat } from '@/composables/chat/useChat'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useProjects } from '@/composables/projects/useProjects'
import { uploadFileToStorage } from '@/utils/uploadFileToStorage'
import { isRichtextRequired } from '@/validators/isRichtextRequired'
import { useUser } from '@/composables/user/useUser'
import { PROFILES_STORAGE_KEY } from '@/composables/user/useUser'
import type { CompletenessResult } from '@/composables/useCompleteness'

export interface ParticipationRequestFormData {
    acceptTerms: boolean
    motivation: string
}

const props = defineProps<{
    project?: Project
    profileCheck?: CompletenessResult
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'confirm', requestSent: boolean): void
}>()
const router = useRouter()
const userStore = useApplicationStore()
const applicationStore = useApplicationStore()
const { userProfile: storeUserProfile } = storeToRefs(applicationStore)
const { createParticipationRequest, resolveParticipationRequestChat } = useProjects()
const { sendMessage } = useChat()
const { updateUserProfile } = useUser()
const { loadOrg } = useOrgs()

const missingFieldsForm = ref<any>(null)
const state = ref<LoadingState>('idle')
const isSubmitting = ref(false)
const trackGtmEvent = ref(false)
const org = ref<Org | undefined>(undefined)
const userProfile = ref<UserProfile | undefined>(undefined)
const formData = ref<ParticipationRequestFormData>({
    acceptTerms: false,
    motivation: ''
})

const validationResults = ref<ValidationResults>({
    isValid: false,
    errors: {}
})


const { open: openTooMuchRequestsModal } = useModal({
    component: TooMuchRequestsModal
})

onMounted(async () => {
    console.log('ProjectRequestModal mounted with props:', props)
    console.log('Project:', props.project)
    console.log('ProfileCheck:', props.profileCheck)
    
    userProfile.value = cloneDeep(storeUserProfile.value || {}) as UserProfile
    if (props.project?.orgId) {
        org.value = await loadOrg(props.project.orgId)
    }
})

const orgTitle = computed((): string | undefined => {
    return org.value?.title
})

const isProfileComplete = computed((): boolean => {
    return props.profileCheck?.isComplete ?? false
})

const missingFields = computed(() => {
    return props.profileCheck?.errors ?? {}
})

async function validateParticipationRequest(formData: ParticipationRequestFormData) {
    return await validate<ParticipationRequestFormData>(formData, {
        motivation: [isRichtextRequired()]
    })
}

const updateProfile = (value: any) => {
    userProfile.value = value
}

const showVolunteerProfilePreview = () => {
    const profile = userProfile.value || storeUserProfile.value
    if (profile) {
        const { open, close } = useModal({
            component: VolunteerProfilePreviewModal,
            attrs: {
                userProfile: profile as UserProfile,
                onClose: () => {
                    close()
                }
            }
        })
        
        open()
    }
}

const submit = async () => {
    console.log('Submit function called!')
    console.log('Project at submit:', props.project)
    isSubmitting.value = true

    validationResults.value = await validateParticipationRequest(formData.value)

    if (!isProfileComplete.value && missingFieldsForm.value) {
        const missingFieldsValidationResults = await missingFieldsForm.value.validate()
        validationResults.value = {
            isValid: validationResults.value.isValid && missingFieldsValidationResults.isValid,
            errors: {
                ...validationResults.value.errors,
                ...missingFieldsValidationResults.errors
            }
        }
    }

    if (!validationResults.value.isValid) {
        isSubmitting.value = false
        return
    }

    try {
        if (!isProfileComplete.value && userProfile.value) {
            const shouldUploadProfileImage =
                userProfile.value.profileImg && userProfile.value.profileImg !== storeUserProfile.value?.profileImg

            if (shouldUploadProfileImage) {
                userProfile.value.profileImg = await uploadFileToStorage(
                    userProfile.value.profileImg!,
                    PROFILES_STORAGE_KEY,
                    userStore.getUserId || ''
                )
            }

            await updateUserProfile(userProfile.value.id, userProfile.value)
            await applicationStore.reloadUserProfile()
        }

        if (!props.project?.id) {
            throw new Error('Project ID is required to submit request')
        }
        
        const request = await createParticipationRequest(props.project.id, formData.value)
        const chat = await resolveParticipationRequestChat(request)
        await sendMessage(chat.id, request.motivation)

        state.value = 'done'
        isSubmitting.value = false
        trackGtmEvent.value = true

        emit('confirm', true)
        router.push({ name: 'dobrovolnictvo-ako-dalej' })
    } catch (e: any) {
        if (e.message === 'Request failed with status code 429') {
            isSubmitting.value = false
            emit('update:modelValue', false)
            await openTooMuchRequestsModal()
            return
        }
        state.value = 'error'
        isSubmitting.value = false
        console.error('Submit project request', e)
    }
}

const goToProfile = () => {
    router.push({ name: 'profile-volunteer-preferences' })
    emit('update:modelValue', false)
}

useHead(() => ({
    script: trackGtmEvent.value
        ? [{ innerHTML: `window.dataLayer = window.dataLayer || []; window.dataLayer.push({event: 'user_request', request_type: 'support_project'})` }]
        : []
}))
</script>

<style lang="postcss">
.link-icon:after {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    vertical-align: middle;
    background: no-repeat center/contain
        url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 20 20"><g style="fill:none;stroke:rgb(54,49,201);stroke-width:1"><line x1="5" y1="5" x2="5" y2="14" /><line x1="14" y1="9" x2="14" y2="14" /><line x1="5" y1="14" x2="14" y2="14" /><line x1="5" y1="5" x2="9" y2="5"  /><line x1="10" y1="2" x2="17" y2="2"  /><line x1="17" y1="2" x2="17" y2="9" /><line x1="10" y1="9" x2="17" y2="2" style="stroke-width:1.5" /></g></svg>');
}
</style>
